import { useQuery } from "@tanstack/react-query";
import { fetchGeodataBundle } from "../api/fetchGeodataBundle";

const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

function cacheKey(lat, lng) {
  return `odcg_geodata_${lat.toFixed(4)}_${lng.toFixed(4)}`;
}

function readFreshCache(lat, lng) {
  try {
    const raw = localStorage.getItem(cacheKey(lat, lng));
    if (!raw) return null;
    const { at, payload } = JSON.parse(raw);
    if (!at || !payload) return null;
    const age = Date.now() - new Date(at).getTime();
    if (age > CACHE_TTL_MS) return null;
    return { at, payload, age };
  } catch {
    return null;
  }
}

function writeCache(lat, lng, payload) {
  const at = new Date().toISOString();
  try {
    localStorage.setItem(cacheKey(lat, lng), JSON.stringify({ at, payload }));
  } catch {
    /* quota */
  }
  return at;
}

async function fetchGeodataWithRecovery(lat, lng) {
  const cached = readFreshCache(lat, lng);

  try {
    const data = await fetchGeodataBundle(lat, lng);
    const at = writeCache(lat, lng, data);
    const degraded = !!data._fatalFallback;
    return {
      data,
      fromCache: false,
      lastOkAt: at,
      err: degraded ? "Failed (CORS/Network). Using fallback/cached." : "",
      liveOk: !degraded,
    };
  } catch (e) {
    console.error("fetchGeodataWithRecovery", e);
    if (cached) {
      return {
        data: cached.payload,
        fromCache: true,
        lastOkAt: cached.at,
        err: `Failed (CORS/Network). Using cached. ${e.message || ""}`.trim(),
        liveOk: false,
      };
    }
    throw e;
  }
}

/**
 * Live environmental bundle: TanStack Query, 2h localStorage TTL, 15min poll only after a successful live fetch.
 */
export function useGeodataQuery(lat, lng, pollMinutes = 15) {
  const intervalMs = Math.min(Math.max(pollMinutes, 5), 15) * 60 * 1000;

  return useQuery({
    queryKey: ["geodata", lat, lng],
    queryFn: () => fetchGeodataWithRecovery(lat, lng),
    staleTime: 60 * 1000,
    gcTime: 1000 * 60 * 60 * 6,
    retry: 3,
    retryDelay: 1000,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d?.liveOk) return false;
      return intervalMs;
    },
    refetchIntervalInBackground: false,
  });
}
